// Suscripciones de notificaciones Telegram por usuario × indicador × symbol ×
// timeframe (Standard E.2/E.3). Módulo singleton con transporte inyectable —
// patrón calcado de hooks/remotePreferences.js: ninguna página lo inicializa =>
// queda inerte y la sección de notificaciones ni siquiera se renderiza.
//
// Estructura persistida v2 (bajo STORAGE_KEY en localStorage y, vía transporte,
// en la cuenta del usuario del chart público):
//   {
//     "_version": 2,
//     "<indicatorId>": {
//       "BTCUSDT": { "enabled": true, "timeframes": ["15m", "1h"] }
//     }
//   }
// Símbolo canónico: mayúsculas, sin "/" ('BTC/USDT' → 'BTCUSDT'). La v1
// (plana, sin símbolo) migra envolviéndose bajo DEFAULT_SYMBOL (BTCUSDT).

export const SUPPORTED_TFS = ['15m', '1h', '4h', '1d']
export const TF_LABELS = { '15m': '15m', '1h': '1h', '4h': '4h', '1d': '1D', '5m': '5m' }

// Timeframes por indicador cuando difieren del set global de arriba. Ausente
// = usa SUPPORTED_TFS tal cual (cero cambio para los indicadores existentes).
// Espejo exacto de PER_INDICATOR_TIMEFRAMES en
// backend/app/schemas/telegram_subscriptions.py — NASDAQ Quantum es
// 5m-nativo (decisión de producto 2026-09-23, WP conexión de señales, v1).
export const PER_INDICATOR_TFS = { nasdaq_quantum: ['5m'] }
export function tfsFor(indicatorId) {
  return PER_INDICATOR_TFS[indicatorId] || SUPPORTED_TFS
}
// DEBE coincidir exactamente con ALLOWED_INDICATORS del backend
// (app/schemas/telegram_subscriptions.py) — cualquier indicador aquí que el
// backend no reconozca hace fallar el PUT ENTERO con 400 para TODOS los
// indicadores (el payload es un único dict con todas las claves, "extra
// forbid" del lado servidor). Encontrado 2026-08-14 (P2 de rango_quantum,
// validación en pantalla de Marc): 'patron_ruptura_quantum' llevaba aquí
// desde antes de este indicador y el backend nunca lo soportó (solo
// supertrend_quantum tiene motor de señales real, ver
// signal_card_tasks.py::_SIGNAL_INDICATORS) — rompía el toggle de
// notificaciones de CUALQUIER indicador, no solo el suyo. Ver
// docs/BACKLOG.md para el seguimiento de patron_ruptura_quantum/
// showict_quantum (paneles que muestran el toggle sin backend real detrás).
export const SUPPORTED_INDICATORS = ['supertrend_quantum', 'rango_quantum', 'nasdaq_quantum']
export const STORAGE_KEY = 'telegram_subscriptions'
export const SUBSCRIPTIONS_VERSION = 2
export const DEFAULT_SYMBOL = 'BTCUSDT'

const SYMBOL_RE = /^[A-Z0-9]{3,30}$/

// 'BTC/USDT' → 'BTCUSDT'. Devuelve '' si la entrada no es un string.
export function toCanonicalSymbol(symbol) {
  if (typeof symbol !== 'string') return ''
  return symbol.trim().toUpperCase().replace(/\//g, '')
}

export function isValidSymbol(symbol) {
  return SYMBOL_RE.test(symbol)
}

export const SAFE_DEFAULTS = Object.freeze({
  _version: SUBSCRIPTIONS_VERSION,
  ...Object.fromEntries(SUPPORTED_INDICATORS.map((id) => [id, Object.freeze({})])),
})

// Transporte inyectable: la página que lo usa (chart público) llama a
// initTelegramNotificationsApi con funciones que devuelven promesas axios.
// Sin inicialización, getTelegramNotificationsApi() devuelve null.
let api = null

// Interfaz que el servicio consume del transporte. El init valida que esté
// COMPLETA (fail-fast): un método ausente rompería en caliente con un
// "x is not a function" — exactamente el bug de 2026-07-25 (publicApi no
// tenía .put). Si añades métodos al servicio, añádelos aquí y al contrato.
export const TRANSPORT_METHODS = Object.freeze([
  'getStatus',
  'getSubscriptions',
  'putSubscriptions',
  'createLinkToken',
])

export function initTelegramNotificationsApi(impl) {
  if (impl) {
    const missing = TRANSPORT_METHODS.filter((m) => typeof impl[m] !== 'function')
    if (missing.length) {
      throw new Error(`telegramNotifications: transporte incompleto, faltan: ${missing.join(', ')}`)
    }
  }
  api = impl || null
}

export function getTelegramNotificationsApi() {
  return api
}

// Adaptador único chart público → transporte del servicio. ChartPublicPage lo
// usa tal cual; el test de contrato lo ejercita contra el publicApi REAL para
// que una interfaz que no coincida (p.ej. método ausente en el cliente) rompa
// el test y no la validación del usuario.
export function createPublicChartTransport(client) {
  return {
    getStatus: () => client.get('/telegram-status').then((r) => r.data),
    getSubscriptions: () => client.get('/telegram-subscriptions').then((r) => r.data),
    putSubscriptions: (subs) => client.put('/telegram-subscriptions', { subscriptions: subs }).then((r) => r.data),
    createLinkToken: () => client.post('/telegram-link-token').then((r) => r.data),
  }
}

// Normaliza un dict de suscripciones ya en v2: claves desconocidas fuera,
// símbolos canonicalizados y validados por formato, enabled coaccionado a
// booleano y timeframes filtrados al set soportado (sin duplicados y en el
// orden canónico de SUPPORTED_TFS).
function normalize(raw) {
  const out = { _version: SUBSCRIPTIONS_VERSION }
  for (const id of SUPPORTED_INDICATORS) {
    const entry = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw[id] : undefined
    const symbols = {}
    if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
      for (const [rawSymbol, conf] of Object.entries(entry)) {
        const symbol = toCanonicalSymbol(rawSymbol)
        if (!isValidSymbol(symbol)) continue
        const enabled = !!(conf && typeof conf === 'object' && conf.enabled === true)
        const timeframes =
          conf && typeof conf === 'object' && Array.isArray(conf.timeframes)
            ? tfsFor(id).filter((tf) => conf.timeframes.includes(tf))
            : []
        symbols[symbol] = { enabled, timeframes }
      }
    }
    out[id] = symbols
  }
  return out
}

// Migraciones incrementales por _version (E.3). v1 → v2: cada entrada plana
// {enabled, timeframes} se envuelve bajo DEFAULT_SYMBOL (las suscripciones
// existentes se asocian al activo actual/inferido; sin dato, BTCUSDT).
// Versión desconocida → {} (default seguro).
function migrate(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  if (raw._version === SUBSCRIPTIONS_VERSION) return raw
  if (raw._version === 1) {
    const out = {}
    for (const id of SUPPORTED_INDICATORS) {
      const entry = raw[id]
      if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
        out[id] = { [DEFAULT_SYMBOL]: entry }
      }
    }
    return { _version: SUBSCRIPTIONS_VERSION, ...out }
  }
  return {}
}

// E.2 — cualquier entrada (undefined/null/{}/legacy/tipos rotos) produce una
// estructura segura y completa; nunca devuelve undefined. Se aplica en AMBOS
// caminos de carga (localStorage y remoto).
export function resolveTelegramSubscriptions(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return normalize({})
  return normalize(migrate(raw))
}

export function loadLocalSubscriptions() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return resolveTelegramSubscriptions(raw ? JSON.parse(raw) : {})
  } catch {
    return resolveTelegramSubscriptions({})
  }
}

export function saveLocalSubscriptions(value) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(resolveTelegramSubscriptions(value)))
  } catch { /* localStorage lleno/bloqueado: no rompemos la UI */ }
}

// ─── Helpers de celda (puros, inmutables) ────────────────────────────────────

function getSymbolEntry(subs, indicator, symbol) {
  const entry = subs?.[indicator]
  if (!entry || typeof entry !== 'object') return null
  const conf = entry[toCanonicalSymbol(symbol)]
  return conf && typeof conf === 'object' ? conf : null
}

// Una celda (indicator, symbol, tf) está activa si el símbolo está enabled y
// el TF está en su lista.
export function isCellEnabled(subs, indicator, symbol, tf) {
  const conf = getSymbolEntry(subs, indicator, symbol)
  return !!(conf && conf.enabled && conf.timeframes.includes(tf))
}

// Activa/desactiva la combinación symbol×TF. Al activar, el símbolo queda
// enabled; al desactivar, solo se quita el TF (el resto de TFs sigue activo).
export function toggleCell(subs, indicator, symbol, tf) {
  const canonical = toCanonicalSymbol(symbol)
  const entry = subs[indicator] || {}
  const conf = entry[canonical] || { enabled: false, timeframes: [] }
  const on = !isCellEnabled(subs, indicator, canonical, tf)
  const timeframes = on
    ? tfsFor(indicator).filter((t) => t === tf || conf.timeframes.includes(t))
    : conf.timeframes.filter((t) => t !== tf)
  return {
    ...subs,
    [indicator]: {
      ...entry,
      [canonical]: { enabled: on ? true : conf.enabled, timeframes },
    },
  }
}

// Activa/desactiva TODAS las combinaciones de un símbolo (sus TFs se conservan).
export function setSymbolEnabled(subs, indicator, symbol, enabled) {
  const canonical = toCanonicalSymbol(symbol)
  const entry = subs[indicator] || {}
  const conf = entry[canonical] || { enabled: false, timeframes: [] }
  return {
    ...subs,
    [indicator]: { ...entry, [canonical]: { ...conf, enabled: !!enabled } },
  }
}

// Símbolos suscritos a un indicador, en orden alfabético.
export function listSymbols(subs, indicator) {
  const entry = subs?.[indicator]
  if (!entry || typeof entry !== 'object') return []
  return Object.keys(entry).sort()
}
